/**
 * Design System §3.2 named this component and its contract ("24-grid outline, stroke 2,
 * `aria-hidden`, label mandatory") before it existed. This is that component, not an adoption of
 * an external icon font: every glyph below is drawn here, `stroke="currentColor"`, so it inherits
 * whatever text color it sits in and needs no asset to keep in sync across themes.
 *
 * Brand's SVG-only rule (`brand/BRAND.md`) governs the *mark* — the isotype and wordmark — which
 * this never touches. A department glyph in a nav rail is UI, not the identity.
 */

const PATHS = {
  broadcasts: 'M4 6h13v10H4z M17 9l4-2v8l-4-2',
  trends: 'M4 16l5-6 4 4 7-9 M20 5h-4v4',
  alerts: 'M6 16v-5a6 6 0 0 1 12 0v5l2 3H4z M10 20a2 2 0 0 0 4 0',
  processing: 'M8 4h8l4 4v12H4V4z M9 12h6 M9 16h6',
  voices: 'M9 3h6v9a3 3 0 0 1-6 0z M6 11a6 6 0 0 0 12 0 M12 17v4 M9 21h6',
  channels: 'M12 21c4-4 7-7.5 7-11a7 7 0 1 0-14 0c0 3.5 3 7 7 11z M12 13a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  /** A newspaper: the copies a subscription brings in. */
  posts: 'M4 5h12v14H5a1 1 0 0 1-1-1z M16 9h4v8a2 2 0 0 1-4 0z M7 8h6 M7 12h6 M7 16h4',
  /** The broadcast arcs of a feed icon — the convention every reader uses, so it reads as
   *  "subscription" without a label. */
  feeds: 'M5 19a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M5 13a6 6 0 0 1 6 6 M5 7a12 12 0 0 1 12 12',
  // A receipt with a torn edge: the ledger of what the machine spent.
  costs: 'M6 3h12v18l-3-2-3 2-3-2-3 2z M9 8h6 M9 12h6 M9 16h3',
  people: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M3 20c1-3.5 3.5-5 6-5s5 1.5 6 5 M17 8a2.5 2.5 0 1 1 0 5 M17 13c1.7 0 4 1 5 3.5',
  flow: 'M6 5a2 2 0 1 0 0 4 2 2 0 0 0 0-4z M18 15a2 2 0 1 0 0 4 2 2 0 0 0 0-4z M6 9v3a3 3 0 0 0 3 3h6a3 3 0 0 1 3 3',
  /** Three sliders at different rests — a threshold being tuned, for the relevance-score setting. */
  tune: 'M4 6h2 M10 6h10 M6 6a2 2 0 1 0 4 0 2 2 0 0 0-4 0 M4 12h8 M16 12h4 M12 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0 M4 18h2 M10 18h10 M6 18a2 2 0 1 0 4 0 2 2 0 0 0-4 0',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M21 21l-5-5',
  play: 'M8 5.5v13l10-6.5z',
  check: 'M4.5 12.5l5 5 10-11',
  replay: 'M4 11a8 8 0 1 1 2.2 5.6 M4 11V5 M4 11h6',
  dashboard: 'M4 4h7v7H4z M13 4h7v4h-7z M13 12h7v8h-7z M4 15h7v5H4z',
  chevronLeft: 'M14.5 6l-6 6 6 6',
  chevronRight: 'M9.5 6l6 6-6 6',
  /** Points down at rest — the details/disclosure affordance, rotating to point up when open. */
  expandMore: 'M6 9.5l6 6 6-6',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, label }: { name: IconName; label?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      className="icon"
    >
      {label && <title>{label}</title>}
      <path d={PATHS[name]} />
    </svg>
  );
}
